/**
 * The typed API client, and the only place in the application that calls `fetch` — components go
 * through TanStack Query hooks, which go through here (CLAUDE.md §4.7).
 *
 * Why it is shaped the way it is: `src/lib/design-notes.md`.
 */

import type { SupportedLocale } from '@/lib/locale';

/** Column metadata, as declared in the report's `report.yaml` and echoed in every response. */
export interface ReportColumn {
  field: string;
  label: string;
  /** `percent` is a 0–100 value (`12.34` means 12.34%) — the one numeric type shown with decimals. */
  type: 'string' | 'number' | 'integer' | 'percent' | 'boolean' | 'date' | 'datetime' | 'status';
  /**
   * Declared heading this column sits under, for a report wide enough to need a second header
   * row. Sent by the server (§5.7) rather than worked out here — the client renders the structure
   * it is given and infers none of it from the values (§4.3).
   */
  group?: string | null;
  /**
   * Whether this field is on the report's server-side `sort:` whitelist (§5.4). The client cannot
   * work this out and must not assume it — a pivoted report whitelists five of two hundred columns.
   */
  sortable?: boolean;
}

export interface SortSpec {
  field: string;
  /** An enum, never a string like "efficiency DESC" — the server composes the SQL (§5.4). */
  dir: 'asc' | 'desc';
}

export type ReportFilterScalar = string | number | boolean | null;

export interface ReportQueryRequest {
  /**
   * Which governed view of the report to run (§5.1). Omit it for a report that declares no
   * `views:` — the server reads absent as the report's `default_view`, and naming a view on a
   * report that has none is a 400 rather than an ignored key.
   */
  view?: string;
  filters: Record<
    string,
    ReportFilterScalar | Array<Exclude<ReportFilterScalar, null>>
  >;
  sort?: SortSpec[];
  page?: { offset: number; limit: number };
}

/**
 * The response envelope every report shares — CLAUDE.md §5.7. `generatedAt` is when this response
 * was produced; `dataAsOf` is the latest source refresh it represents. Never present the first as
 * data freshness — it would be quietly wrong on every report between warehouse syncs.
 */
export interface ReportQueryResponse<TRow = Record<string, unknown>> {
  /**
   * Which view produced these rows, echoed back so a response is self-describing (§5.1). Null for
   * a report that declares no views — a client that never sends one never has to read one back.
   */
  view?: string | null;
  columns: ReportColumn[];
  rows: TRow[];
  total: number;
  /**
   * Whether the server's row cap cut this result short (§5.1, §5.7). `total` cannot answer this on
   * its own, and §4.3 makes it the condition for a client-side totals row and for a pager count
   * that says "of N" rather than "N loaded".
   */
  truncated: boolean;
  generatedAt: string;
  dataAsOf: string | null;
}

/** RFC 9457 problem detail — the one failure shape for the whole API (§5.8). */
export interface ProblemDetail {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  /** Log reference. SQL text and stack traces never cross this boundary. */
  reference?: string;
}

export class ApiError extends Error {
  // Declared and assigned rather than written as constructor parameter properties: those are
  // TypeScript-only syntax that has to be transpiled away, which `erasableSyntaxOnly` forbids.
  readonly problem: ProblemDetail;
  readonly response: Response;

  constructor(problem: ProblemDetail, response: Response) {
    super(problem.title);
    this.name = 'ApiError';
    this.problem = problem;
    this.response = response;
  }
}

const BASE_URL = '/winsight';

/** The build version the API reported on the most recent call. Never auto-reload on it (§6.5). */
let serverBuildVersion: string | null = null;

export function getServerBuildVersion(): string | null {
  return serverBuildVersion;
}

/**
 * The first build version this tab ever saw, and whether the API has moved off it since.
 *
 * **The baseline is the API's own first answer, never `__BUILD_VERSION__`** (§6.5): the two repos
 * report different kinds of value and are never equal, which pinned the prompt on permanently.
 * The question is only "has the API this tab talks to been replaced under it". Sticky once set,
 * because a rolling deploy serves both builds and the next response may report the old one again.
 */
let firstSeenBuildVersion: string | null = null;
let apiBuildChanged = false;

/** True once the API has reported a build version different from the first one this tab saw. */
export function hasApiBuildChanged(): boolean {
  return apiBuildChanged;
}

/**
 * The bearer token, held in `sessionStorage` for the life of the tab and no longer (§6.7). Never
 * `localStorage` (outlives the tab, hands a shared floor terminal's next user a live session),
 * never a cookie (CSRF surface for a header-only API), never the URL (history, logs, `Referer`).
 */
const TOKEN_STORAGE_KEY = 'winsight.token';

function readStoredToken(): string | null {
  try {
    const stored = window.sessionStorage.getItem(TOKEN_STORAGE_KEY);
    return stored === null || stored === '' ? null : stored;
  } catch {
    // Storage can be unavailable (kiosk profile, private mode, framing); costs a refresh, not the app.
    return null;
  }
}

let authToken: string | null = readStoredToken();

/** The token as the last call would send it. Read by the provider to seed its state. */
export function getAuthToken(): string | null {
  return authToken;
}

export function setAuthToken(token: string | null): void {
  authToken = token;
  try {
    if (token === null) {
      window.sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    }
  } catch {
    // Same as above — an unwritable store costs a refresh, not the session.
  }
}

async function send(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }
  if (init.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  if (authToken !== null) {
    // Header only — never a query string, which reaches history, access logs and `Referer` (§6.7).
    headers.set('Authorization', `Bearer ${authToken}`);
  }

  const response = await fetch(`${BASE_URL}${path}`, { ...init, headers });

  const reportedVersion = response.headers.get('X-Build-Version');
  if (reportedVersion !== null) {
    serverBuildVersion = reportedVersion;
    if (firstSeenBuildVersion === null) {
      firstSeenBuildVersion = reportedVersion;
    } else if (reportedVersion !== firstSeenBuildVersion) {
      apiBuildChanged = true;
    }
  }

  if (!response.ok) {
    throw new ApiError(await readProblem(response), response);
  }

  return response;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  return (await (await send(path, init)).json()) as T;
}

/** For endpoints that answer 204, or whose body carries nothing the client acts on. */
async function requestNoContent(path: string, init: RequestInit = {}): Promise<void> {
  await send(path, init);
}

async function readProblem(response: Response): Promise<ProblemDetail> {
  try {
    return (await response.json()) as ProblemDetail;
  } catch {
    // A non-JSON body means a gateway answered, not us; synthesise so callers handle one shape.
    return {
      type: 'about:blank',
      title: response.statusText || 'Request failed',
      status: response.status,
    };
  }
}

/*
  Authentication — CLAUDE.md §6.7. Base Portal is the sole authority; this application verifies
  tokens and never issues one. A restored token is re-verified on the next `/auth/me` rather than
  trusted on sight, so an expired or revoked one lands the user on the login form.
*/

/** The verified identity, as the server reports it from Base Portal's `/api/token` response. */
export interface AuthUser {
  userId: string;
  username: string;
  /** The person's display name from Base Portal. */
  name: string;
  /** Base Portal roles. Report visibility is enforced server-side; these only shape the menu. */
  roles: string[];
  /**
   * Login site / factory code (Base Portal `companyCode`, legacy `User.defaultFactory`).
   * Used to open factory filters on the reader's home site when the URL has no factory set.
   */
  defaultFactory?: string | null;
  /** Base Portal's normalized, profile-owned language preference. */
  locale: 'en' | 'zh-CN' | 'vi';
}

/**
 * A username and password on their way to Base Portal, and nowhere else. The password exists for
 * the duration of one call: never stored, never logged, never in a problem detail, never echoed
 * back — this application compares no passwords and keeps no local password store (§6.7).
 */
export interface Credentials {
  username: string;
  password: string;
}

export interface LoginResponse {
  /** Issued by Base Portal, relayed by the server. Header-only from here on. */
  token: string;
  user: AuthUser;
}

/**
 * Exchange a credential for a token; the form authenticates nobody. 401 means the credential was
 * rejected, 503 means Base Portal was unreachable — never report the second as a bad password.
 */
export function login(credentials: Credentials): Promise<LoginResponse> {
  return request('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  });
}

/** Who the current token belongs to. Used to restore session state when a token is in memory. */
export function fetchCurrentUser(): Promise<AuthUser> {
  return request('/api/auth/me');
}

/**
 * Sign out. There is no server-side session to end — logout is a client-side discard plus cache
 * eviction (§6.7); the caller drops the token whether this call succeeds or not.
 */
export function logout(): Promise<void> {
  return requestNoContent('/api/auth/logout', { method: 'POST' });
}

export function queryReport<TRow = Record<string, unknown>>(
  reportId: string,
  body: ReportQueryRequest,
  locale: SupportedLocale,
): Promise<ReportQueryResponse<TRow>> {
  return request(`/api/reports/${encodeURIComponent(reportId)}/query`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Accept-Language': locale },
  });
}

/** One selectable value of a filter, and what the reader sees instead of it. */
export interface FilterOptionDto {
  value: string;
  label: string;
}

export interface FilterOptionsResponse {
  report: string;
  filter: string;
  options: FilterOptionDto[];
  generatedAt: string;
}

/**
 * A filter's selectable values, read from its own source table — CLAUDE.md §5.4. `values` is every
 * filter the page holds: which of them narrow this list is declared server-side under `scope:`, so
 * the client sends all and the server keeps the ones it named. Nulls are dropped, so an unchosen
 * filter applies no scope predicate.
 */
export function fetchFilterOptions(
  reportId: string,
  filterName: string,
  values: Record<string, string | null> = {},
): Promise<FilterOptionsResponse> {
  const params = new URLSearchParams();
  for (const [name, value] of Object.entries(values)) {
    if (value !== null && value !== '') {
      params.set(name, value);
    }
  }
  const query = params.toString();
  return request(
    `/api/reports/${encodeURIComponent(reportId)}/filters/${encodeURIComponent(filterName)}/options`
      + (query === '' ? '' : `?${query}`),
  );
}

export type ExportFormat = 'xlsx' | 'csv';

/**
 * The export request: everything `/query` takes, plus the layout the reader is looking at (§5.9).
 * `columns` is presentation state — the server re-runs the query itself and resolves every field
 * against the view's declaration. It does not narrow rows: header filters are the §4.4 "within
 * loaded rows" exception and stay in the browser.
 */
export interface ReportExportRequest extends ReportQueryRequest {
  columns?: { field: string; label: string; group?: string }[];
}

/** A finished export: the file itself and the name the server gave it. */
export interface ExportedFile {
  blob: Blob;
  filename: string;
}

/**
 * Stream a report's SQL result as a file — §5.9 when the on-screen grid *is* the query.
 * Failures before the stream opens arrive as problem details and throw `ApiError` like any
 * other call.
 */
export async function exportReport(
  reportId: string,
  format: ExportFormat,
  body: ReportExportRequest,
  locale: SupportedLocale,
): Promise<ExportedFile> {
  const response = await send(
    `/api/reports/${encodeURIComponent(reportId)}/export?format=${format}`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { Accept: '*/*', 'Accept-Language': locale },
    },
  );
  return {
    blob: await response.blob(),
    filename:
      contentDispositionFilename(response.headers.get('Content-Disposition')) ??
      `${reportId}.${format}`,
  };
}

/** The `filename=` the server set — quoted or bare — so the download keeps its given name. */
function contentDispositionFilename(header: string | null): string | null {
  if (header === null) {
    return null;
  }
  const match = /filename\*?=(?:"([^"]+)"|([^;]+))/i.exec(header);
  const raw = (match?.[1] ?? match?.[2])?.trim();
  return raw === undefined || raw === '' ? null : raw;
}
